<template>
  <m-section v-if="props.allowRequests" title="Opening Times">
    <m-radio-group v-model="selectedOption" :label="submissionTimesLabel" required>
      <m-radio
        v-model="selectedOption"
        val="weekdays"
        label="Same hours Monday–Friday (no weekends)"
      />
      <m-radio v-model="selectedOption" val="all" label="Always open (including weekends)" />
      <m-radio v-model="selectedOption" val="custom" label="Custom schedule" />
    </m-radio-group>

    <div v-if="selectedOption === 'weekdays'">
      <m-labeled-text label="What hours on weekdays?" />
      <m-input-group class="q-mt-sm" horizontal left>
        <m-time v-model="weekdayOpeningTime" label="Start Time" medium required />
        <m-time v-model="weekdayClosingTime" label="End Time" medium required />
      </m-input-group>
    </div>

    <div v-if="selectedOption === 'custom'">
      <div v-for="(dayName, index) in allDays" :key="index" class="q-mb-lg">
        <h3>{{ dayName }}</h3>
        <m-link
          v-if="timePeriodsByDay(index + 1).length === 0"
          text="Add a period"
          inline
          @click="addPeriod(dayName)"
        />
        <div v-else>
          <m-form-repeat-group
            v-for="(period, idx) in timePeriodsByDay(index + 1)"
            :key="idx"
            class="q-mb-sm"
            @close="removeTimePeriodFromDay(period)"
          >
            <m-labeled-text :label="`Period ${idx + 1}`" />
            <m-input-group horizontal left>
              <m-time v-model="period.openingTime" label="Start Time" medium required />
              <m-time v-model="period.closingTime" label="End Time" medium required />
            </m-input-group>
          </m-form-repeat-group>
          <m-link text="Add another period" inline @click="addPeriod(dayName)" />
        </div>
      </div>
    </div>
  </m-section>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import MSection from "../../MSection";
import MRadioGroup from "../../MRadioGroup";
import { MRadio, MTime } from "../../inputs";
import MLabeledText from "../..//MLabeledText";
import MInputGroup from "../..//MInputGroup";
import MLink from "../../MLink";
import MFormRepeatGroup from "../../MFormRepeatGroup";

interface OpeningPeriod {
  dayOfWeek: string;
  openingTime: string;
  closingTime: string;
  isOpen: boolean;
}

const props = defineProps<{
  submissionTimesLabel?: string;
  openingPeriodDetails?: OpeningPeriod[];
  allowRequests: boolean;
}>();

const emit = defineEmits<{
  (e: "update:openingPeriods", value: OpeningPeriod[]): void;
  (e: "update:alwaysOpen", value: boolean): void;
}>();

const openingPeriods = ref<OpeningPeriod[]>([]);
const selectedOption = ref<string | null>(null);
const weekdayOpeningTime = ref("");
const weekdayClosingTime = ref("");
const allDays = ref(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]);

onMounted(() => {
  openingPeriods.value = (props.openingPeriodDetails || []).map((period) => ({
    dayOfWeek: period.dayOfWeek,
    openingTime: period.openingTime,
    closingTime: period.closingTime,
    isOpen: !!(period.openingTime && period.closingTime),
  }));

  if (checkWeekdaysOnlySelectedPreviously()) {
    selectedOption.value = "weekdays";
    const first = openingPeriods.value[0];
    weekdayOpeningTime.value = first.openingTime;
    weekdayClosingTime.value = first.closingTime;
  } else {
    selectedOption.value = checkIfAlwaysOpenSelectedPreviously() ? "all" : "custom";
  }
});

watch([weekdayOpeningTime, weekdayClosingTime], applyWeekdayTimes);
watch(
  openingPeriods,
  (newPeriods) => {
    emit("update:openingPeriods", newPeriods);
  },
  { deep: true },
);
watch(selectedOption, (newVal) => {
  emit("update:alwaysOpen", newVal === "all");
});

watch(
  () => props.allowRequests,
  (newVal) => {
    if (!newVal) {
      openingPeriods.value = [];
    }
  },
);

function applyWeekdayTimes() {
  if (selectedOption.value === "weekdays" && weekdayOpeningTime.value && weekdayClosingTime.value) {
    const weekdays = ["1", "2", "3", "4", "5"];
    openingPeriods.value = weekdays.map((day) => ({
      dayOfWeek: day,
      openingTime: weekdayOpeningTime.value,
      closingTime: weekdayClosingTime.value,
      isOpen: true,
    }));
  }
}

function checkWeekdaysOnlySelectedPreviously() {
  if (openingPeriods.value.length !== 5) return false;
  const first = openingPeriods.value[0];
  return openingPeriods.value.every(
    (p) => p.isOpen && p.openingTime === first.openingTime && p.closingTime === first.closingTime,
  );
}

function checkIfAlwaysOpenSelectedPreviously() {
  return openingPeriods.value.length === 0;
}

function timePeriodsByDay(day: number) {
  return openingPeriods.value.filter((period) => period.dayOfWeek === String(day));
}

function removeTimePeriodFromDay(periodToRemove: OpeningPeriod) {
  const index = openingPeriods.value.indexOf(periodToRemove);
  if (index !== -1) openingPeriods.value.splice(index, 1);
}

function addPeriod(dayName: string) {
  const dayIndex = allDays.value.indexOf(dayName);
  openingPeriods.value.push({
    dayOfWeek: String(dayIndex + 1),
    openingTime: "",
    closingTime: "",
    isOpen: true,
  });
}
</script>
